import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { validateAnswer } from '../../utils/answerValidator';
import { generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import CodeEditor from '../CodeEditor/CodeEditor';
import McqScreen from './McqScreen';
import './ActivityScreen.css';

const ActivityScreen = ({ screen, onAnswer, userResponse, lessonId, subjectArea }) => {
  const { handleRequest } = useApi();
  const [activityState, setActivityState] = useState(userResponse || {});
  const [validationResult, setValidationResult] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(!!userResponse);
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [code, setCode] = useState('');
  const [codeValidationResult, setCodeValidationResult] = useState(null);

  // Parse activity type from contentForTeacherOrSystem
  // For now, support simple fill-in-the-blank and matching
  const parseActivity = () => {
    const content = screen.contentForTeacherOrSystem || '';
    
    // Check for fill-in-the-blank pattern: text with ____
    if (content.includes('____')) {
      return {
        type: 'fill-blank',
        text: content
      };
    }
    
    // Check for matching pattern (lines with -> or : separator)
    if (content.includes('->') || content.includes(':')) {
      const lines = content.split('\n').filter(line => line.trim());
      const pairs = lines.map(line => {
        const parts = line.split(/->|:/).map(p => p.trim());
        return { left: parts[0], right: parts[1] || '' };
      });
      return {
        type: 'matching',
        pairs
      };
    }
    
    // Default: drag and drop or categorization
    return {
      type: 'categorize',
      content
    };
  };

  const activity = parseActivity();

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setCodeValidationResult(null);
    
    // Load saved code from localStorage
    if (lessonId && screen.id) {
      const savedCode = localStorage.getItem(`lesson-code-${lessonId}-${screen.id}`);
      if (savedCode) {
        setCode(savedCode);
      }
    }
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest]);

  // Auto-save code to localStorage as user types (debounced)
  useEffect(() => {
    if (lessonId && screen.id && code !== undefined) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(`lesson-code-${lessonId}-${screen.id}`, code);
      }, 500); // Debounce: save 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }
  }, [code, lessonId, screen.id]);

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: code
      });
      setCodeValidationResult(response.data);
      if (onAnswer) {
        onAnswer(code, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  const handleFillBlank = (blankIndex, value) => {
    const newState = { ...activityState, [`blank-${blankIndex}`]: value };
    setActivityState(newState);
  };

  const handleMatching = (leftItem, rightItem) => {
    const newState = { ...activityState, [leftItem]: rightItem };
    setActivityState(newState);
  };

  const handleSubmit = () => {
    const checkingLogic = screen.checkingLogic || {};
    const result = validateAnswer(activityState, checkingLogic);
    
    setValidationResult(result);
    setHasAnswered(true);
    
    if (onAnswer) {
      onAnswer(activityState, result);
    }
  };

  const renderFillBlank = () => {
    const parts = activity.text.split('____');
    return (
      <div className="fill-blank-activity">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span>{part}</span>
            {index < parts.length - 1 && (
              <input
                type="text"
                value={activityState[`blank-${index}`] || ''}
                onChange={(e) => handleFillBlank(index, e.target.value)}
                className="blank-input"
                disabled={hasAnswered}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderMatching = () => {
    return (
      <div className="matching-activity">
        {activity.pairs.map((pair, index) => (
          <div key={index} className="matching-pair">
            <span className="left-item">{pair.left}</span>
            <span className="arrow">→</span>
            <input
              type="text"
              value={activityState[pair.left] || ''}
              onChange={(e) => handleMatching(pair.left, e.target.value)}
              className="right-input"
              placeholder="Match here"
              disabled={hasAnswered}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCategorize = () => {
    return (
      <div className="categorize-activity">
        <p>{activity.content}</p>
        <p className="activity-note">This activity type will be enhanced with drag-and-drop functionality.</p>
      </div>
    );
  };

  return (
    <div className="activity-screen">
      <div className="activity-content">
        <div className="activity-panel">
          <h3 className="activity-title">{screen.title}</h3>
          <p className="action-description">{screen.studentActionDescription}</p>

          <div className="activity-area">
            {activity.type === 'fill-blank' && renderFillBlank()}
            {activity.type === 'matching' && renderMatching()}
            {activity.type === 'categorize' && renderCategorize()}
          </div>

          {validationResult && (
            <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              {validationResult.feedback}
            </div>
          )}

          {!hasAnswered && (
            <button
              className="submit-activity-button"
              onClick={handleSubmit}
            >
              {screen.uiLayoutHints?.primaryButtonLabel || 'Sjekk svar'}
            </button>
          )}

          {/* Generate Task Buttons */}
          {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
            <div className="generate-buttons">
              {subjectArea === 'programming' && !showCodingTask && (
                <button
                  className="generate-button generate-coding"
                  onClick={handleGenerateCodingTask}
                  disabled={loading.coding}
                >
                  {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
                </button>
              )}
              {!showMCQ && !generatedTasks.mcq && (
                <button
                  className="generate-button generate-mcq"
                  onClick={handleGenerateMCQ}
                  disabled={loading.mcq}
                >
                  {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
                </button>
              )}
            </div>
          )}

          {/* Generated Coding Task */}
          {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
            <div className="generated-task coding-task">
              <div className="generated-task-left">
                <h3>Kodeoppgave</h3>
                <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
                <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
              </div>
              <div className="generated-task-right">
                <CodeEditor
                  value={code}
                  onChange={setCode}
                  language="javascript"
                  height="500px"
                />
                {codeValidationResult && (
                  <div className={`feedback ${codeValidationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                    {codeValidationResult.feedback}
                  </div>
                )}
                <button
                  className="submit-code-button"
                  onClick={handleSubmitCode}
                  disabled={loading.coding || !code.trim()}
                >
                  {loading.coding ? 'Validerer...' : 'Kjør kode'}
                </button>
                {generatedTasks.coding.expectedOutput && (
                  <div className="expected-output-generated">
                    <strong>Forventet resultat:</strong>
                    <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generated MCQ */}
          {showMCQ && generatedTasks.mcq && (
            <div className="generated-task mcq-task">
              <McqScreen
                screen={generatedTasks.mcq}
                onAnswer={handleMCQAnswer}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityScreen;
